/**
 * PostHog Analytics for Reframer
 *
 * Tracks user behavior to understand product usage.
 * Privacy-focused: no PII captured, anonymous user IDs.
 */

import posthog from 'posthog-js'

// PostHog configuration
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY || ''
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com'

/**
 * Initialize PostHog analytics
 * Call this once at app startup
 */
export function initAnalytics() {
  if (!POSTHOG_KEY) {
    console.log('PostHog key not configured, analytics disabled')
    return
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Privacy settings
    persistence: 'localStorage',
    autocapture: false, // We'll track specific events manually
    capture_pageview: true, // Auto-track page views
    capture_pageleave: true,
    disable_session_recording: true, // No session recordings for privacy
    // Respect Do Not Track
    respect_dnt: true,
  })
}

/**
 * Identify user (anonymous ID from our auth)
 */
export function identifyUser(userId: string) {
  if (!POSTHOG_KEY) return
  posthog.identify(userId)
}

/**
 * Reset identity on logout
 */
export function resetAnalytics() {
  if (!POSTHOG_KEY) return
  posthog.reset()
}

// ============================================================================
// Event Tracking Functions
// ============================================================================

/**
 * Track when user starts a new session
 */
export function trackSessionStarted(mode: 'voice' | 'text') {
  if (!POSTHOG_KEY) return
  posthog.capture('session_started', { mode })
}

/**
 * Track when user completes recording/input
 */
export function trackInputCompleted(mode: 'voice' | 'text', durationMs?: number) {
  if (!POSTHOG_KEY) return
  posthog.capture('input_completed', { mode, duration_ms: durationMs })
}

/**
 * Track when AI structures the session
 */
export function trackAIStructured() {
  if (!POSTHOG_KEY) return
  posthog.capture('ai_structured')
}

/**
 * Track when user saves a session
 */
export function trackSessionSaved(hasDistortions: boolean, emotionDelta: number) {
  if (!POSTHOG_KEY) return
  posthog.capture('session_saved', {
    has_distortions: hasDistortions,
    emotion_delta: emotionDelta,
  })
}

/**
 * Track when user views their history
 */
export function trackHistoryViewed() {
  if (!POSTHOG_KEY) return
  posthog.capture('history_viewed')
}

/**
 * Track when user views insights
 */
export function trackInsightsViewed() {
  if (!POSTHOG_KEY) return
  posthog.capture('insights_viewed')
}

/**
 * Track when user requests theme discovery
 */
export function trackThemeDiscoveryRequested() {
  if (!POSTHOG_KEY) return
  posthog.capture('theme_discovery_requested')
}

/**
 * Track when crisis overlay is shown
 */
export function trackCrisisOverlayShown() {
  if (!POSTHOG_KEY) return
  posthog.capture('crisis_overlay_shown')
}

/**
 * Track feature usage
 */
export function trackFeatureUsed(feature: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return
  posthog.capture('feature_used', { feature, ...properties })
}
